import React from "react";
import ls from "local-storage";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/ar-ma";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/ar-ma";
import { DEFAULT_LANGUAGE, LANGUAGES } from "./languages";

/**
 * Translations of the backoffice.
 *
 *   locales/<lang>/<namespace>.json   one file per part of the app (common, nav, moncompte…)
 *   t("moncompte:profile.title")      namespace before « : », then the key (nested with dots)
 *   t("common:save")                  « common: » can be left out: t("save")
 *   t("nav:Mon compte")               flat keys work too (the menu is keyed by its French label)
 *   t("x.items", { count: 3 })        plural: x.items_one / x.items_other (Intl plural rules: ar has zero/two/few/many)
 *   t("x.hello", { name: "Sara" })    "Bonjour {{name}}"
 *   tr("x.scan", { plus: <b>+</b> })  same, but the values can be React elements (bold words in a sentence)
 *   tm("x.help")                      the text may hold <strong>, <b>, <em>, <i>, <u> and <br/> (help texts)
 *
 * Missing in the current language -> French -> `defaultValue` -> the key itself.
 * Changing language reloads the page: the old class pages, antd, dayjs and moment all pick it up at once.
 */

// every locales/<lang>/<namespace>.json is bundled; a new file is a new namespace, nothing to register
const files = require.context("./locales", true, /^\.\/[a-z]{2}\/[\w-]+\.json$/);
const resources = {};
files.keys().forEach((path) => {
  const [, lang, ns] = path.match(/^\.\/([a-z]{2})\/([\w-]+)\.json$/);
  (resources[lang] = resources[lang] || {})[ns] = files(path);
});

const STORAGE_KEY = "lang";

const stored = ls.get(STORAGE_KEY);
const current = LANGUAGES.some((l) => l.code === stored) ? stored : DEFAULT_LANGUAGE;

export const getLanguage = () => current;
export const languageInfo = (code = current) => LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
export const isRtl = () => languageInfo().dir === "rtl";

export function setLanguage(code) {
  if (code === current || !LANGUAGES.some((l) => l.code === code)) return;
  ls.set(STORAGE_KEY, code);
  window.location.reload();
}

// applied once, before any page renders
dayjs.locale(languageInfo().dayjs);
moment.locale(languageInfo().moment);
if (typeof document !== "undefined") {
  document.documentElement.lang = current;
  document.documentElement.dir = languageInfo().dir;
}

const plurals = {};
const pluralForm = (lang, n) => {
  if (!plurals[lang]) plurals[lang] = new Intl.PluralRules(languageInfo(lang).intl);
  return plurals[lang].select(n);
};

const lookup = (lang, ns, key) => {
  const table = resources[lang] && resources[lang][ns];
  if (!table) return undefined;
  if (typeof table[key] === "string") return table[key];
  const v = key.split(".").reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), table);
  return typeof v === "string" ? v : undefined;
};

const find = (lang, ns, key, count) => {
  if (count != null) {
    const v = lookup(lang, ns, `${key}_${pluralForm(lang, Number(count))}`) ?? lookup(lang, ns, `${key}_other`);
    if (v !== undefined) return v;
  }
  return lookup(lang, ns, key);
};

const warned = {};

export function t(fullKey, params = {}) {
  const raw = String(fullKey);
  const sep = raw.indexOf(":");
  const ns = sep > 0 ? raw.slice(0, sep) : "common";
  const key = (sep > 0 ? raw.slice(sep + 1) : raw).trim();
  let text = find(current, ns, key, params.count);
  if (text === undefined && current !== DEFAULT_LANGUAGE) text = find(DEFAULT_LANGUAGE, ns, key, params.count);
  if (text === undefined) {
    if (process.env.NODE_ENV === "development" && params.defaultValue === undefined && !warned[raw]) {
      warned[raw] = true;
      console.warn(`[i18n] missing key ${raw}`);
    }
    text = params.defaultValue !== undefined ? String(params.defaultValue) : key;
  }
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) => (params[name] != null ? params[name] : m));
}

/** t() whose {{values}} may be React elements: returns an array to render. */
export function tr(fullKey, values = {}) {
  const strings = {};
  Object.keys(values).forEach((k) => {
    if (!React.isValidElement(values[k])) strings[k] = values[k];
  });
  const text = t(fullKey, strings);
  return text.split(/\{\{\s*(\w+)\s*\}\}/).map((part, i) =>
    i % 2 ? <React.Fragment key={i}>{values[part] !== undefined ? values[part] : `{{${part}}}`}</React.Fragment> : part
  );
}

const RICH = /<(strong|b|em|i|u)>([\s\S]*?)<\/\1>|<br\s*\/?>/g;

/** t() whose text may contain simple inline tags: returns an array to render (no HTML injection). */
export function tm(fullKey, params = {}) {
  const text = t(fullKey, params);
  const out = [];
  let last = 0;
  let m;
  RICH.lastIndex = 0;
  while ((m = RICH.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(m[1] ? React.createElement(m[1], { key: out.length }, m[2]) : <br key={out.length} />);
    last = RICH.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Hook form for function components: const { t, lang } = useTranslation(); */
export const useTranslation = () => ({ t, lang: current, dir: languageInfo().dir });
